import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LazyScrollableDataSource } from '@rappi/common';
import { SelectComponent } from '@rappi/ui/select';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { SelectOption } from '../../purchase-order-detail/sections/section-base.types';
import { FH_SELECT_KEYS } from '../../company-form/company-form.constants';

export type FhSelectValue = string | string[] | null;
export type FhOptionResolver = (ids: string[]) => Observable<SelectOption[]>;

export interface FhSelectConfig {
  search: boolean;
  includeAll: boolean;
  showTotalsChip: boolean;
  maxNumberOfChips: number;
  panelClass: string;
}

// A real ControlValueAccessor around `one-select`.
//
// `one-select` cannot be driven by formControlName on its own: the `writeValue` it inherits from
// ControlValueAccessorConnector only assigns an internal `_value` and never touches
// `defaultValue`/`selection`, so patching a bound control leaves the UI blank. On top of that
// `setSelection()` writes the WHOLE option object into the control rather than the id. That is why
// every other financial-hub section uses it unbound with `[dataSource]` + `(selectionChanged)` and
// re-syncs imperatively via a `poLoaded` + setTimeout dance.
//
// This wrapper hides both problems: the outer control always holds an id (or an array of ids), and
// `writeValue` drives the inner select imperatively. The dynamic company form can therefore bind
// every field type the same way.
@Component({
  selector: 'app-fh-select',
  templateUrl: './fh-select.component.html',
  styleUrls: ['./fh-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FhSelectComponent),
      multi: true,
    },
  ],
})
export class FhSelectComponent implements ControlValueAccessor, OnChanges, AfterViewInit, OnDestroy {
  /* istanbul ignore next -- decorator metadata */
  @Input() label = '';
  /* istanbul ignore next -- decorator metadata */
  @Input() multiple = false;
  /* istanbul ignore next -- decorator metadata */
  @Input() search = false;
  /** Static options. Also seeds the resolution cache so writeValue resolves synchronously. */
  /* istanbul ignore next -- decorator metadata */
  @Input() options: SelectOption[] = [];
  /** Remote source. Takes precedence over `options` when present. */
  /* istanbul ignore next -- decorator metadata */
  @Input() dataSource: LazyScrollableDataSource<SelectOption> | null = null;
  /**
   * Resolves ids the cache does not know yet. Needed because LazyScrollableDataSource.connect()
   * requires a CollectionViewer, so this component cannot read the loaded page itself — on edit
   * the saved id may live on a page the select has not fetched.
   */
  /* istanbul ignore next -- decorator metadata */
  @Input() optionResolver: FhOptionResolver | null = null;

  /* istanbul ignore next -- decorator metadata */
  @ViewChild('select') select: SelectComponent<SelectOption>;

  readonly KEYS = FH_SELECT_KEYS;

  isDisabled = false;

  // True only while this component is driving the inner select. `setSelection`,
  // `setMultipleSelection` and `clearSelection` all emit `selectionChanged` SYNCHRONOUSLY, so
  // without this flag every programmatic write would loop straight back into onChange.
  private _writing = false;
  private _viewReady = false;
  private _value: FhSelectValue = null;
  private _pending: FhSelectValue = null;
  private _hasPending = false;
  private readonly optionCache = new Map<string, SelectOption>();
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly _cd: ChangeDetectorRef) {}

  get config(): FhSelectConfig {
    return {
      search: this.search,
      includeAll: false,
      showTotalsChip: this.multiple,
      maxNumberOfChips: this.multiple ? 3 : 1,
      panelClass: '',
    };
  }

  get source(): LazyScrollableDataSource<SelectOption> | SelectOption[] {
    return this.dataSource ?? this.options;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this._indexOptions(this.options);
      // A value written before the options arrived can now be resolved.
      if (this._viewReady && this._value !== null) {
        this._applySelection(this._value);
      }
    }
  }

  ngAfterViewInit(): void {
    this._viewReady = true;
    if (this._hasPending) {
      this._hasPending = false;
      this._applySelection(this._pending);
      this._pending = null;
      // writeValue can land during the parent's change detection pass.
      this._cd.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── ControlValueAccessor ────────────────────────────────────────────────────────────────────

  writeValue(value: FhSelectValue): void {
    this._value = value ?? null;
    if (!this._viewReady) {
      this._pending = this._value;
      this._hasPending = true;
      return;
    }
    this._applySelection(this._value);
  }

  registerOnChange(fn: (value: FhSelectValue) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  // ─── Inner select ────────────────────────────────────────────────────────────────────────────

  onSelectionChanged(event: SelectOption | SelectOption[]): void {
    if (this._writing) {
      return;
    }
    if (this.multiple) {
      const selected = (event as SelectOption[]) ?? [];
      selected.forEach(option => this.cacheOption(option));
      this._value = selected.map(option => option.id);
    } else {
      const selected = event as SelectOption;
      this.cacheOption(selected);
      this._value = selected ? selected.id : null;
    }
    this._onChange(this._value);
    this._onTouched();
  }

  // ─── Internals ───────────────────────────────────────────────────────────────────────────────

  private _applySelection(value: FhSelectValue): void {
    if (!this.select) {
      return;
    }
    const ids = this._toIds(value);
    const missing = ids.filter(id => !this.optionCache.has(id));

    if (missing.length && this.optionResolver) {
      this.optionResolver(missing)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe(options => {
          this._indexOptions(options);
          this._commitSelection(this._toIds(this._value));
          this._cd.markForCheck();
        });
    }

    // Commit what is already resolvable so the box is never blank while a lookup is in flight.
    this._commitSelection(ids);
  }

  private _commitSelection(ids: string[]): void {
    if (!this.select) {
      return;
    }
    this._writing = true;
    try {
      if (this.multiple) {
        this._commitMultiple(ids);
      } else {
        this._commitSingle(ids);
      }
    } finally {
      this._writing = false;
    }
  }

  private _commitSingle(ids: string[]): void {
    if (!ids.length) {
      this.select.clearSelection();
      return;
    }
    const option = this.optionCache.get(ids[0]);
    if (option) {
      this.select.setSelection(option);
      return;
    }
    // Unresolved id (remote source, page not loaded, no resolver): show the id itself rather than
    // an empty box. For owner_search_service the id IS the label, so this is exact.
    this.select.defaultValue = { id: ids[0], name: ids[0] };
  }

  private _commitMultiple(ids: string[]): void {
    // setMultipleSelection TOGGLES rather than assigns, so only touch the symmetric difference.
    const target = new Set(ids);
    const current = new Set((this.select.selection?.selected ?? []).map(option => option.id));

    current.forEach(id => {
      if (!target.has(id)) {
        const option = this.optionCache.get(id);
        if (option) {
          this.select.setMultipleSelection(option);
        }
      }
    });
    target.forEach(id => {
      if (!current.has(id)) {
        const option = this.optionCache.get(id);
        if (option) {
          this.select.setMultipleSelection(option);
        }
      }
    });
    this.select.defaultValue = this.select.selection?.selected[0] ?? null;
  }

  private _toIds(value: FhSelectValue): string[] {
    if (value === null || value === undefined) {
      return [];
    }
    if (Array.isArray(value)) {
      return value.map(id => String(id));
    }
    return [String(value)];
  }

  private _indexOptions(options: SelectOption[]): void {
    (options ?? []).forEach(option => this.cacheOption(option));
  }

  private cacheOption(option: SelectOption | null | undefined): void {
    if (option && option.id !== undefined && option.id !== null) {
      this.optionCache.set(String(option.id), option);
    }
  }

  private _onChange: (value: FhSelectValue) => void = () => {};
  private _onTouched: () => void = () => {};
}
